import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Card, Button, Form, Row, Col, Alert, Spinner, Badge } from 'react-bootstrap';
import api, { formatApiErrorDetail } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import Footer from '../components/Footer';

const PostularCotizacion = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  
  const [convocatoria, setConvocatoria] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    plazo_entrega: '',
    validez_cotizacion: '',
    // text fields removed; now files only for docs
  });
  
  const [files, setFiles] = useState({
    cotizacion_pdf: null,
    declaracion_postor_pdf: null,
    registro_nacional_proveedores_pdf: null,
    ficha_tecnica_pdf: null
  });

  useEffect(() => {
    if (!isAuthenticated) {
      // Guardar la ruta de destino para volver después del login
      localStorage.setItem('redirectAfterLogin', `/postular-cotizacion/${id}`);
      navigate('/login');
      return;
    }
    loadConvocatoria();
  }, [id, isAuthenticated]);

  const loadConvocatoria = async () => {
    try {
      const response = await api.get(`/api/v1/convocatorias/public/${id}`);
      setConvocatoria(response.data);
    } catch (error) {
      console.error('Error loading convocatoria:', error);
      setError('Error al cargar la convocatoria');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleFileChange = (e) => {
    const { name, files: fileList } = e.target;
    if (fileList && fileList.length > 0) {
      setFiles({ ...files, [name]: fileList[0] });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    setSubmitting(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('nro_convocatoria', id);
      formDataToSend.append('plazo_entrega', formData.plazo_entrega);
      formDataToSend.append('validez_cotizacion', formData.validez_cotizacion);
      
      if (files.cotizacion_pdf) {
        formDataToSend.append('cotizacion_pdf', files.cotizacion_pdf);
      }
      
      if (files.declaracion_postor_pdf) {
        formDataToSend.append('declaracion_postor_pdf', files.declaracion_postor_pdf);
      }

      if (files.registro_nacional_proveedores_pdf) {
        formDataToSend.append('registro_nacional_proveedores_pdf', files.registro_nacional_proveedores_pdf);
      }

      if (files.ficha_tecnica_pdf) {
        formDataToSend.append('ficha_tecnica_pdf', files.ficha_tecnica_pdf);
      }

      await api.post('/api/v1/cotizaciones/', formDataToSend);

      setSuccess(true);
      setTimeout(() => {
        navigate('/mis-cotizaciones');
      }, 1500);
    } catch (error) {
      console.error('Error submitting cotizacion:', error);
      setError(formatApiErrorDetail(error, 'Error al enviar la cotización'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f5f7fa', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="text-center">
          <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
          <p className="mt-3 text-muted">Cargando...</p>
        </div>
      </div>
    );
  }

  if (!convocatoria) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f5f7fa' }}>
        <Container className="py-5">
          <Alert variant="danger">
            <Alert.Heading>Error</Alert.Heading>
            <p>No se pudo cargar la convocatoria.</p>
            <Button onClick={() => navigate('/')}>Volver al Inicio</Button>
          </Alert>
        </Container>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f5f7fa' }}>
      {/* Header */}
      <div style={{ 
        backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.25), rgba(0, 0, 0, 0.25)), url(/fondo.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        padding: '2rem 0',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        position: 'relative'
      }}>
        <Container>
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center">
              <img 
                src="/LOGO-DIRESA.png" 
                alt="Logo DIRESA" 
                className="me-3"
                style={{ 
                  height: '90px', 
                  objectFit: 'contain',
                  filter: 'brightness(0) invert(1)'
                }}
              />
              <div>
                <h2 className="text-white mb-1 fw-bold">Postular Cotización</h2>
                <p className="text-white-50 mb-0 small">
                  Convocatoria: {convocatoria.COD_CONVOCATORIA || convocatoria.nro_convocatoria}
                </p>
              </div>
            </div>
            <Button
              variant="outline-light"
              onClick={() => navigate(`/convocatoria-details/${id}`)}
            >
              <i className="bi bi-arrow-left me-2"></i>
              Volver
            </Button>
          </div>
        </Container>
      </div>

      {/* Contenido */}
      <Container className="py-5">
        {/* Resumen de Convocatoria */}
        <Card className="border-0 shadow-sm mb-4">
          <Card.Body className="p-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h4 className="fw-bold mb-0" style={{ color: '#2b3e53' }}>
                <i className="bi bi-info-circle me-2"></i>
                Resumen de la Convocatoria
              </h4>
              <Badge bg={convocatoria.estado === 'ABIERTA' ? 'success' : convocatoria.estado === 'CERRADA' ? 'secondary' : convocatoria.estado === 'EVALUACION' ? 'warning' : 'info'} className="px-3 py-2">
                {convocatoria.estado}
              </Badge>
            </div>
            <Row className="g-3">
              <Col md={6} lg={3}>
                <div className="mb-2 d-flex align-items-center">
                  <i className="bi bi-box-seam me-2" style={{ color: '#6c757d', fontSize: '0.875rem' }}></i>
                  <small className="text-muted fw-semibold me-2">Tipo:</small>
                  <span className="text-dark">
                    {convocatoria.tipo_bien === 'S' ? 'Servicio' : convocatoria.tipo_bien === 'B' ? 'Bienes' : 'N/A'}
                  </span>
                </div>
              </Col>
              <Col md={6} lg={3}>
                <div className="mb-2 d-flex align-items-center">
                  <i className="bi bi-calendar-year me-2" style={{ color: '#6c757d', fontSize: '0.875rem' }}></i>
                  <small className="text-muted fw-semibold me-2">Año:</small>
                  <span className="text-dark">{convocatoria.ano_eje || 'N/A'}</span>
                </div>
              </Col>
              <Col md={6} lg={3}>
                <div className="mb-2 d-flex align-items-center">
                  <i className="bi bi-hash me-2" style={{ color: '#6c757d', fontSize: '0.875rem' }}></i>
                  <small className="text-muted fw-semibold me-2">Nro Pedido:</small>
                  <span className="text-dark">{convocatoria.nro_pedido || 'N/A'}</span>
                </div>
              </Col>
              {convocatoria.nombre_ejecutora && (
                <Col md={6} lg={3}>
                  <div className="mb-2 d-flex align-items-center">
                    <i className="bi bi-building me-2" style={{ color: '#6c757d', fontSize: '0.875rem' }}></i>
                    <small className="text-muted fw-semibold me-2">Unidad Ejecutora:</small>
                    <span className="text-dark">{convocatoria.nombre_ejecutora}</span>
                  </div>
                </Col>
              )}
            </Row>
            {convocatoria.motivo_pedido && (
              <div className="mt-3 p-3 border rounded" style={{ backgroundColor: '#f8f9fa' }}>
                <div className="d-flex align-items-center mb-2">
                  <i className="bi bi-file-text me-2" style={{ color: '#2b3e53' }}></i>
                  <strong style={{ color: '#2b3e53' }}>Motivo del Pedido:</strong>
                </div>
                <p className="text-dark mb-0 small">{convocatoria.motivo_pedido}</p>
              </div>
            )}
          </Card.Body>
        </Card>

        {/* Formulario */}
        <Card className="border-0 shadow-sm">
          <Card.Body className="p-4">
            {error && (
              <Alert variant="danger" dismissible onClose={() => setError(null)}>
                <i className="bi bi-exclamation-triangle me-2"></i>
                {error}
              </Alert>
            )}
            
            {success && (
              <Alert variant="success">
                <i className="bi bi-check-circle me-2"></i>
                ¡Cotización enviada exitosamente! Redirigiendo...
              </Alert>
            )}

            <Form onSubmit={handleSubmit}>
              <Row className="g-4">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">
                      <i className="bi bi-calendar-check me-2"></i>
                      Plazo de Entrega (días) <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="number"
                      name="plazo_entrega"
                      value={formData.plazo_entrega}
                      onChange={handleInputChange}
                      required
                      min="1"
                      placeholder="Ingrese el plazo en días"
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">
                      <i className="bi bi-calendar-range me-2"></i>
                      Validez de Cotización (días) <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="number"
                      name="validez_cotizacion"
                      value={formData.validez_cotizacion}
                      onChange={handleInputChange}
                      required
                      min="1"
                      placeholder="Ingrese la validez en días"
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">
                      <i className="bi bi-file-earmark-text me-2"></i>
                      Registro Nacional de Proveedores (PDF)
                    </Form.Label>
                    <Form.Control
                      type="file"
                      name="registro_nacional_proveedores_pdf"
                      accept=".pdf"
                      onChange={handleFileChange}
                    />
                    <Form.Text className="text-muted">
                      Suba constancia o documento de RNP (PDF)
                    </Form.Text>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">
                      <i className="bi bi-file-earmark-text me-2"></i>
                      Ficha Técnica (PDF)
                    </Form.Label>
                    <Form.Control
                      type="file"
                      name="ficha_tecnica_pdf"
                      accept=".pdf"
                      onChange={handleFileChange}
                    />
                    <Form.Text className="text-muted">
                      Suba la ficha técnica en PDF
                    </Form.Text>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">
                      <i className="bi bi-file-pdf me-2"></i>
                      Cotización PDF
                    </Form.Label>
                    <Form.Control
                      type="file"
                      name="cotizacion_pdf"
                      accept=".pdf"
                      onChange={handleFileChange}
                    />
                    <Form.Text className="text-muted">
                      Suba el archivo PDF de su cotización
                    </Form.Text>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">
                      <i className="bi bi-file-earmark-text me-2"></i>
                      Declaración de Postor (PDF)
                    </Form.Label>
                    <Form.Control
                      type="file"
                      name="declaracion_postor_pdf"
                      accept=".pdf"
                      onChange={handleFileChange}
                    />
                    <Form.Text className="text-muted">
                      Suba la declaración jurada del postor (PDF)
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              <div className="d-flex justify-content-end gap-3 mt-4">
                <Button
                  variant="secondary"
                  onClick={() => navigate(`/convocatoria-details/${id}`)}
                  disabled={submitting}
                >
                  Cancelar
                </Button>
                <Button
                  variant="primary"
                  type="submit"
                  disabled={submitting}
                  style={{ backgroundColor: '#2b3e53', borderColor: '#2b3e53' }}
                >
                  {submitting ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-send me-2"></i>
                      Enviar Cotización
                    </>
                  )}
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </Container>
      <Footer />
    </div>
  );
};

export default PostularCotizacion;

